import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import Modal from "@/components/ui/Modal";
import { useLapCandidates } from "@/hooks/useLapComparison";
import type {
  LapCandidate,
  LapCandidateFilters,
  LapCandidateOrdering,
  LapCandidateScope,
  LapRef,
} from "@/types";
import { formatDateShort } from "@/utils/dateFormat";
import { sameLapRef } from "@/utils/lapRef";
import { formatLapTime } from "@/utils/timeFormat";
import { dateInputToIso, parseLapTimeInput, vehicleLabel } from "./comparisonMath";

interface LapPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  trackId: string;
  trackName?: string;
  /** Lap currently in the slot being replaced. */
  currentRef: LapRef | null;
  /** Lap in the other slot (shown as taken, still selectable). */
  otherRef: LapRef | null;
  onSelect: (ref: LapRef) => void;
}

const PAGE_SIZE = 25;
const SCOPES: LapCandidateScope[] = ["mine", "team", "public"];

export default function LapPickerModal({
  isOpen,
  onClose,
  trackId,
  trackName,
  currentRef,
  otherRef,
  onSelect,
}: LapPickerModalProps) {
  const { t } = useTranslation("pages");
  const [scope, setScope] = useState<LapCandidateScope>("mine");
  const [driverId, setDriverId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [maxLapTime, setMaxLapTime] = useState("");
  const [includeInvalid, setIncludeInvalid] = useState(false);
  const [orderBy, setOrderBy] = useState<LapCandidateOrdering>("conductedAt");
  const [offset, setOffset] = useState(0);

  const parsedMaxLapTime = useMemo(() => parseLapTimeInput(maxLapTime), [maxLapTime]);
  const maxLapTimeMs = parsedMaxLapTime ?? undefined;
  const maxLapTimeInvalid = maxLapTime.trim() !== "" && parsedMaxLapTime === null;

  // A new track (reference replaced) or a fresh open starts at page one.
  useEffect(() => {
    setOffset(0);
  }, [trackId, isOpen]);

  const filters = useMemo<LapCandidateFilters | null>(
    () =>
      isOpen && trackId
        ? {
            trackId,
            scope,
            driverId: driverId || undefined,
            vehicleId: vehicleId || undefined,
            from: dateInputToIso(from, "start"),
            to: dateInputToIso(to, "end"),
            maxLapTimeMs,
            includeInvalid,
            orderBy,
            limit: PAGE_SIZE,
            offset,
          }
        : null,
    [isOpen, trackId, scope, driverId, vehicleId, from, to, maxLapTimeMs, includeInvalid, orderBy, offset],
  );

  const { data, isLoading, isError, isFetching } = useLapCandidates(filters);
  const items = data?.items ?? [];
  const total = data?.meta.total ?? 0;
  const facets = data?.facets ?? { drivers: [], vehicles: [] };

  const resetPage = () => setOffset(0);
  const changeScope = (next: LapCandidateScope) => {
    setScope(next);
    setDriverId("");
    setVehicleId("");
    resetPage();
  };

  const select = (item: LapCandidate) => {
    onSelect({ sessionId: item.sessionId, lapNumber: item.lapNumber });
    onClose();
  };

  const inputClass =
    "w-full rounded-md border border-secondary-300 px-2 py-1.5 text-sm text-secondary-900 focus:border-primary-500 focus:outline-none";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t("lapComparison.picker.title")} size="xl">
      <div className="space-y-4">
        {trackName && (
          <p className="text-sm text-secondary-600">
            {t("lapComparison.picker.trackHint", { track: trackName })}
          </p>
        )}

        <div className="flex gap-1 border-b border-secondary-200" role="tablist">
          {SCOPES.map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={scope === s}
              onClick={() => changeScope(s)}
              className={`px-3 py-2 text-sm border-b-2 transition-colors ${
                scope === s
                  ? "border-primary-600 text-primary-600 font-medium"
                  : "border-transparent text-secondary-600 hover:text-secondary-900"
              }`}
            >
              {t(`lapComparison.picker.scope_${s}`)}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <label className="text-xs text-secondary-600">
            {t("lapComparison.picker.driver")}
            <select
              className={inputClass}
              value={driverId}
              onChange={(e) => {
                setDriverId(e.target.value);
                resetPage();
              }}
            >
              <option value="">{t("lapComparison.picker.allDrivers")}</option>
              {facets.drivers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-secondary-600">
            {t("lapComparison.picker.vehicle")}
            <select
              className={inputClass}
              value={vehicleId}
              onChange={(e) => {
                setVehicleId(e.target.value);
                resetPage();
              }}
            >
              <option value="">{t("lapComparison.picker.allVehicles")}</option>
              {facets.vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-secondary-600">
            {t("lapComparison.picker.from")}
            <input
              type="date"
              className={inputClass}
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                resetPage();
              }}
            />
          </label>
          <label className="text-xs text-secondary-600">
            {t("lapComparison.picker.to")}
            <input
              type="date"
              className={inputClass}
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                resetPage();
              }}
            />
          </label>
          <label className="text-xs text-secondary-600">
            {t("lapComparison.picker.maxLapTime")}
            <input
              type="text"
              inputMode="text"
              placeholder="1:55.000"
              aria-invalid={maxLapTimeInvalid}
              className={`${inputClass} ${maxLapTimeInvalid ? "border-red-400" : ""}`}
              value={maxLapTime}
              onChange={(e) => {
                setMaxLapTime(e.target.value);
                resetPage();
              }}
            />
          </label>
          <label className="text-xs text-secondary-600">
            {t("lapComparison.picker.sort")}
            <select
              className={inputClass}
              value={orderBy}
              onChange={(e) => {
                setOrderBy(e.target.value as LapCandidateOrdering);
                resetPage();
              }}
            >
              <option value="conductedAt">{t("lapComparison.picker.sortDate")}</option>
              <option value="lapTime">{t("lapComparison.picker.sortTime")}</option>
            </select>
          </label>
          <label className="col-span-2 flex items-end gap-2 pb-1.5 text-sm text-secondary-700">
            <input
              type="checkbox"
              checked={includeInvalid}
              onChange={(e) => {
                setIncludeInvalid(e.target.checked);
                resetPage();
              }}
            />
            {t("lapComparison.picker.includeInvalid")}
          </label>
        </div>

        <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-secondary-200">
          {isLoading ? (
            <div className="p-6 text-center text-sm text-secondary-500">
              {t("lapComparison.loading")}
            </div>
          ) : isError ? (
            <div className="p-6 text-center text-sm text-red-600">
              {t("lapComparison.picker.loadError")}
            </div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-sm text-secondary-500">
              {t("lapComparison.picker.empty")}
            </div>
          ) : (
            <ul className={`divide-y divide-secondary-100 ${isFetching ? "opacity-60" : ""}`}>
              {items.map((item) => {
                const ref = { sessionId: item.sessionId, lapNumber: item.lapNumber };
                const isCurrent = sameLapRef(ref, currentRef);
                const isOther = sameLapRef(ref, otherRef);
                return (
                  <li key={item.lapId} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 text-secondary-900">
                        <span className="font-medium">{item.session.driver.name}</span>
                        <span className="text-secondary-500">·</span>
                        <span>{vehicleLabel(item.session.vehicle)}</span>
                        {!item.isValid && (
                          <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                            <AlertTriangle className="h-3 w-3" />
                            {t("lapComparison.invalid")}
                          </span>
                        )}
                      </div>
                      <div className="truncate text-xs text-secondary-500">
                        {item.session.title} · {formatDateShort(item.session.conductedAt)} ·{" "}
                        {t("lapComparison.lapNumber", { number: item.lapNumber })}
                        {item.session.channelCount !== null && (
                          <>
                            {" · "}
                            {t("lapComparison.picker.channelsCount", {
                              count: item.session.channelCount,
                            })}
                          </>
                        )}
                        {" · "}
                        {item.session.source.toUpperCase()}
                      </div>
                    </div>
                    <div className="font-mono text-sm text-secondary-900">
                      {formatLapTime(item.lapTimeMs)}
                    </div>
                    <button
                      type="button"
                      onClick={() => select(item)}
                      disabled={isCurrent}
                      className={`rounded-md px-2.5 py-1 text-xs ${
                        isCurrent
                          ? "cursor-default bg-secondary-100 text-secondary-500"
                          : "btn-primary"
                      }`}
                    >
                      {isCurrent
                        ? t("lapComparison.picker.selected")
                        : isOther
                          ? t("lapComparison.picker.selectOther")
                          : t("lapComparison.picker.select")}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between text-xs text-secondary-600">
          <span>{t("lapComparison.picker.results", { count: total })}</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label={t("lapComparison.picker.previousPage")}
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              className="rounded p-1 hover:bg-secondary-100 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span>
              {total === 0 ? 0 : offset + 1}–{Math.min(total, offset + PAGE_SIZE)}
            </span>
            <button
              type="button"
              aria-label={t("lapComparison.picker.nextPage")}
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className="rounded p-1 hover:bg-secondary-100 disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
